import { AbstractRendererImpl } from '../abstract_renderer';
import { OverlayMode, Overlays } from '../overlays/overlay';
import { PageDesc } from '@fullstory/client-api';
import { RawEvent } from '@fullstory/recording-api';
import { ServerOverlays } from './serveroverlay';
import { Dispatcher } from '@fullstory/channel/proxy';
import {
  ObjectInfo,
  ObjectPathPattern,
  ObjectSelectorPattern,
  PlaybackContext,
  PlayPageOptions,
  PlayResult,
  RendererCursor,
  RendererHost,
  SeekResult,
} from '@fullstory/renderer-shared';
import { ServerPageData } from '@fullstory/playback-server';
import { Inspector, InspectorClientStub } from '../page-insights/inspector';
import { Point } from '@fullstory/util/geometry';
import { NativePlatform } from '@fullstory/proto/fs/api/segment';

export class ServerPageRenderer extends AbstractRendererImpl {
  private _divContainer: HTMLDivElement;

  constructor(host: RendererHost, dispatcher: Dispatcher, ctx: PlaybackContext) {
    super(host, dispatcher, ctx);

    this.playbackContainer().e.style.height = '100%';
    const container = this.container();
    const containerDocument = container.ownerDocument || document;
    this._divContainer = containerDocument.createElement('div');
    this._divContainer.style.position = 'relative'; // ensure it renders on top of webviews, which are position:absolute, while also receiving click events in inspect mode
    this._divContainer.style.display = 'flex';
    this._divContainer.style.justifyContent = 'center';
    this._divContainer.style.alignItems = 'center';
    this._divContainer.style.flexDirection = 'column';
    this._divContainer.style.height = '100%';

    container.appendChild(this._divContainer);
    this.scalePlaybackFor(1024, 768, 1);

    window.addEventListener('resize', () => this.resize(), false);
  }

  private resize() {
    const checkboard = document.getElementsByClassName('checkerboard').item(0);
    this.scalePlaybackFor(checkboard.clientWidth, checkboard.clientHeight, 1);
  }

  protected createOverlays(): Overlays {
    return new ServerOverlays(this);
  }

  createInspector(): OverlayMode {
    const inspectorImpl: Inspector = new InspectorClientStub();
    this._dispatcher.RegisterService(inspectorImpl, 'Inspector');
    return { start: () => {}, stop: () => {} };
  }
  createActivityMaps(): OverlayMode {
    const activityMapsImpl = { highlight: () => {}, clear: () => {} };
    this._dispatcher.RegisterService(activityMapsImpl, 'ActivityMaps');
    return { start: () => {}, stop: () => {} };
  }
  async openPage(_page: PageDesc): Promise<void> {
    // Tear down playback data structures from the previous page, if any.
    // TODO(sethf): I don't think we actually reuse renderers between pages
    // anywhere. We should confirm that and then remove the relevant code and
    // just report an error if someone tries.
    this.teardown();
  }
  async playPage(_serverPageData: ServerPageData, _options?: PlayPageOptions): Promise<PlayResult> {
    this._reset();
    // TODO(jordanhurt): remove after the AppPlaybackFirstInsertWhenRefactor flag
    this._host.setFirstInsertWhen(0);
    this._host.onResourceEvent({ kind: 'initialLoadComplete' });
  }
  async playToTime(_pageOffset: number, _requestId?: number | string): Promise<SeekResult> {
    this._reset();
  }
  async onPause(): Promise<void> {
    this._reset();
  }
  appendTrailingEvents(_trailingEvents: RawEvent[]): void {}

  // Hooks for subclasses to adjust rendering / scaling for page insights.
  protected beforeEnteringPageInsights(): void {}
  protected afterLeavingPageInsights(): void {}

  // Returns the container element which we're rendering into. This is owned by
  // WebPageRenderer.
  container(): HTMLDivElement {
    return this.playbackContainer().e as HTMLDivElement;
  }
  private _reset() {
    this._divContainer.innerHTML = '';
    const div = this._divContainer.ownerDocument.createElement('div');
    div.style.marginBottom = '15px';
    div.innerHTML =
      '<svg width="88" height="100" viewBox="0 0 88 100" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
      '<path fill-rule="evenodd" clip-rule="evenodd" d="M36 0C26.3728 0 17.6527 1.6964 11.1562 4.5625C7.908 5.9956 5.1912 7.6927 3.2188 9.75C1.2462 11.8073 0 14.3164 0 17V71C0 73.6836 1.2462 76.1615 3.2188 78.2189C5.1912 80.2762 7.908 82.0044 11.1562 83.4375C17.6527 86.3036 26.3728 88 36 88C37.7294 88 39.4294 87.9453 41.0926 87.8388C40.5563 86.5743 40.1154 85.2595 39.7789 83.9035C38.539 83.9671 37.2779 84 36 84C26.8497 84 18.5902 82.3128 12.7812 79.75C9.8766 78.4686 7.5936 76.9693 6.125 75.4375C4.6564 73.9057 4 72.4586 4 71V60.9688C5.8858 62.6973 8.314 64.1835 11.1562 65.4375C17.6527 68.3036 26.3728 70 36 70C37.3893 70 38.7597 69.9647 40.1072 69.8956C40.534 68.4685 41.0778 67.0918 41.7275 65.7769C39.8691 65.923 37.9549 66 36 66C26.8497 66 18.5902 64.3128 12.7812 61.75C9.8766 60.4686 7.5936 58.9693 6.125 57.4375C4.6564 55.9057 4 54.4586 4 53V42.9688C5.8858 44.6973 8.314 46.1835 11.1562 47.4375C17.6527 50.3036 26.3728 52 36 52C45.6273 52 54.3471 50.3036 60.8438 47.4375C63.6859 46.1835 66.1142 44.6973 68 42.9688V51.1164C69.3661 51.2442 70.7021 51.4757 72 51.803V17C72 14.3164 70.7535 11.8073 68.7812 9.75C66.8088 7.6927 64.0919 5.9956 60.8438 4.5625C54.3472 1.6964 45.6273 0 36 0ZM67.5346 55.0908C69.0729 55.2286 70.5666 55.5213 72 55.9531C81.2573 58.742 88 67.3336 88 77.5C88 89.9264 77.9264 100 65.5 100C56.6456 100 48.9858 94.8854 45.3138 87.4495C44.7062 86.2191 44.2078 84.9252 43.8312 83.5804C43.2896 81.6464 43 79.607 43 77.5C43 74.7011 43.5111 72.0215 44.445 69.5496C45.0239 68.0172 45.7653 66.5645 46.6482 65.2127C50.6643 59.0636 57.6076 55 65.5 55C66.1858 55 66.8644 55.0307 67.5346 55.0908ZM63.9952 59.0603C59.7431 59.4026 55.8969 61.1827 52.9432 63.914C51.4069 65.3345 50.1121 67.0124 49.1272 68.8792C47.7689 71.4535 47 74.3869 47 77.5C47 79.4236 47.2936 81.2786 47.8384 83.0226C48.1073 83.8835 48.4374 84.7174 48.8237 85.5192C49.0357 85.9593 49.2646 86.3897 49.5096 86.8096C49.9561 87.5749 50.456 88.3052 51.0042 88.9955L54.268 85.7317L61.1856 78.8141L67.8646 72.1351L72 67.9997L76.9955 63.0042C75.5009 61.8173 73.8184 60.8567 72 60.1741C69.9778 59.4151 67.7874 59 65.5 59C64.9934 59 64.4915 59.0204 63.9952 59.0603ZM71.3655 74.291L63.5543 82.1023L53.814 91.8426C56.9999 94.4415 61.0679 96 65.5 96C75.7173 96 84 87.7173 84 77.5C84 73.0679 82.4415 68.9999 79.8426 65.814L71.3655 74.291ZM36 4C45.1504 4 53.4096 5.65591 59.2188 8.21881C62.1233 9.50021 64.4064 10.9994 65.875 12.5312C67.3436 14.0631 68 15.5414 68 17C68 18.4586 67.3436 19.9057 65.875 21.4375C64.4064 22.9693 62.1232 24.4686 59.2188 25.75C53.4097 28.3128 45.1504 30 36 30C26.8497 30 18.5902 28.3128 12.7812 25.75C9.8766 24.4686 7.5936 22.9693 6.125 21.4375C4.6564 19.9057 4 18.4586 4 17C4 15.5414 4.6564 14.0631 6.125 12.5312C7.5936 10.9994 9.8766 9.50021 12.7812 8.21881C18.5902 5.65591 26.8497 4 36 4ZM4 24.9688C5.8858 26.6974 8.314 28.1836 11.1562 29.4375C17.6527 32.3036 26.3728 34 36 34C45.6273 34 54.3471 32.3036 60.8438 29.4375C63.6859 28.1836 66.1142 26.6974 68 24.9688V35C68 36.4586 67.3436 37.9057 65.875 39.4375C64.4064 40.9693 62.1232 42.4686 59.2188 43.75C53.4097 46.3128 45.1504 48 36 48C26.8497 48 18.5902 46.3128 12.7812 43.75C9.8766 42.4686 7.5936 40.9693 6.125 39.4375C4.6564 37.9057 4 36.4586 4 35V24.9688Z" fill="#5D6F83"/>\n' +
      '</svg>\n';
    this._divContainer.appendChild(div);

    const title = this._divContainer.ownerDocument.createElement('span');
    title.innerText = "There's stuff happening even though you can't see it!";
    title.style.fontSize = '20px';
    title.style.lineHeight = '30px';
    title.style.color = '#3A4B59';
    this._divContainer.appendChild(title);

    const description = this._divContainer.ownerDocument.createElement('span');
    description.innerText =
      'This page contains only server-side activity, which you can see in the Event List on the right.';
    description.style.fontSize = '14px';
    description.style.lineHeight = '21px';
    description.style.color = '#5D6F83';
    description.style.position = 'relative';
    description.style.top = '5px';
    this._divContainer.appendChild(description);
  }

  teardown() {
    window.removeEventListener('resize', () => this.resize(), false);
  }

  override async invalidateRendererGeometryCaches(): Promise<void> {}

  override queryObjectsAtPoint(_pt: Point): ObjectInfo[] {
    return [];
  }

  override queryPathPattern(_pattern: ObjectPathPattern): ObjectInfo | undefined {
    return undefined;
  }

  override querySelectorPattern(_pattern: ObjectSelectorPattern): ObjectInfo[] {
    return [];
  }

  async waitForImagesToLoad(_maxWaitTimeMs: number) {
    // Do nothing
  }

  override async setCursorOverride(_cursor: RendererCursor): Promise<void> {}

  override async setInteractive(_interactive: boolean): Promise<void> {}

  getNativePlatform(): NativePlatform {
    return NativePlatform.NATIVE_PLATFORM_INVALID;
  }
}
